import dayjs from 'dayjs';
import * as taxApi from '../../lib/api/taxbill';
import BillCollectDialog from '../projectmng/billcollect/BillCollectDialog';
import * as yup from 'yup';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  listTaxbills,
  setTaxbillsFilters,
} from '../../modules/taxbillModule/taxbills';
import { Button, Stack, Typography, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useFormik } from 'formik';

import { useBoolean } from '../../hooks/useBoolean';

import TaxbillList from '../../components/taxbillmng/TaxbillList';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import { TaxbillListBox } from '../../components/taxbillmng/TaxbillListBox';
import TaxbillStatusList from '../../components/taxbillmng/TaxbillStatusList';
import AskDialog from '../../components/common/dialog/AskDialog';
import TaxbillTotalSummary from '../../components/taxbillmng/TaxbillTotalSummary';
import TaxbillEditModal from '../../components/taxbillmng/TaxbillEditModal';

const TaxbillListContainer = () => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  // 결재를 쓰는 설정이면 발행완료는 결재 승인으로만 된다. 화면 버튼이 이 값으로 갈린다.
  const [approvalEnabled, setApprovalEnabled] = useState(false);

  // 수금 내역 다이얼로그 대상. null 이면 닫힌 상태.
  // 매출/매입에 따라 다이얼로그 용어가 달라진다(수금/지급).
  const [collectTarget, setCollectTarget] = useState(null);

  const { totalPrice, taxbills, defaultFilters, error, loading } = useSelector(
    ({ taxbillModule, loading }) => ({
      totalPrice: taxbillModule.taxbills.totalPrice,
      taxbills: taxbillModule.taxbills.taxbills,
      defaultFilters: taxbillModule.taxbills.defaultFilters,
      error: taxbillModule.taxbills.error,
      loading: loading['taxbill/LIST_TAXBILLS'],
    }),
  );

  const [tableData, setTableData] = useState([]);

  const makeFilters = (filters) => {
    const startDate =
      filters.searchStrtDt !== null && filters.searchStrtDt !== ''
        ? dayjs(filters.searchStrtDt).format('YYYY-MM-DD')
        : '';
    const endDate =
      filters.searchEndDt !== null && filters.searchEndDt !== ''
        ? dayjs(filters.searchEndDt).format('YYYY-MM-DD')
        : '';

    const searchData = {
      osTran: filters.searchOsTran,
      invTyp: filters.searchInvTyp,
      searchTyp: filters.searchTyp,
      reqCompYn: filters.searchYn,
      [filters.searchKeyword]: filters.searchWords,
      strtDt: startDate,
      endDt: endDate,
    };

    return searchData;
  };

  useEffect(() => {
    dispatch(listTaxbills(makeFilters(defaultFilters)));
  }, [dispatch, defaultFilters]);

  useEffect(() => {
    if (taxbills) {
      setTableData(taxbills);
    }
  }, [taxbills]);

  const handleFilters = useCallback(
    (name, value) => {
      const newFilters = { ...defaultFilters, [name]: value };

      dispatch(setTaxbillsFilters(newFilters));
    },
    [defaultFilters, dispatch],
  );

  const [selectedItem, setSelectedItem] = useState(null);
  const [itemType, setItemType] = useState(null);
  const [removeVisible, setRemoveVisible] = useState(false);
  const handleRemove = useCallback((row) => {
    setRemoveVisible(true);
    setSelectedItem(row);
  }, []);
  const onRemoveCancle = () => {
    setRemoveVisible(false);
    setSelectedItem(null);
  };
  const onRemoveConfirm = () => {
    //매출 취소
    onRemove();
    setRemoveVisible(false);
    setSelectedItem(null);
  };

  const [selectedCancle, setSelectedCancle] = useState(null);
  const showCancle = useBoolean();
  const handleCancle = useCallback((row) => {
    showCancle.onTrue();
    setSelectedCancle(row);
  }, []);

  const onCancleCancle = () => {
    showCancle.onFalse();
    setSelectedCancle(null);
  };
  const onCancleConfirm = () => {
    //발급 취소
    onCancle();
    showCancle.onFalse();
    setSelectedCancle(null);
  };

  const showEdit = useBoolean();
  const [selectedEdit, setSelectedEdit] = useState(null);
  const handleEdit = (param, type) => {
    setSelectedEdit(param);
    setItemType(type);
    showEdit.onTrue();
  };

  const handleEditClose = () => {
    showEdit.onFalse();
    setSelectedEdit(null);
  };

  const handleEditSave = async (changeDate, type) => {
    try {
      const editData = {
        invUuid: selectedEdit.invUuid,
      };

      // 날짜 필드 설정
      if (type === 'reqComp') {
        editData.reqCompDt = dayjs(changeDate).format('YYYY-MM-DD');
      } else if (type === 'reqPlan') {
        editData.reqPlanDt = dayjs(changeDate).format('YYYY-MM-DD');
      } else if (type === 'rsltComp') {
        editData.rsltCompDt = dayjs(changeDate).format('YYYY-MM-DD');
      }

      const response = await taxApi.editTaxbill(editData);
      if (response.data.info) {
        handleEditClose();
        enqueueSnackbar('수정이 완료 되었습니다', {
          variant: 'success',
        });
        dispatch(listTaxbills(makeFilters(defaultFilters)));
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  const onRemove = async () => {
    try {
      const response = await taxApi.removeIssuedTaxbills(
        selectedItem?.prjUuid,
        selectedItem?.invTyp === '매출' ? 'S' : 'B',
        selectedItem?.invUuid,
      );
      if (response.data.result === 'success') {
        enqueueSnackbar('삭제 되었습니다', {
          variant: 'success',
        });
        dispatch(listTaxbills(makeFilters(defaultFilters)));
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  const onCancle = async () => {
    try {
      const editData = {
        invUuid: selectedCancle.invUuid,
        reqCompYn: 'N',
      };
      const response = await taxApi.editTaxbill(editData);
      if (response.data.info) {
        enqueueSnackbar('발급 취소 되었습니다', {
          variant: 'success',
        });
        dispatch(listTaxbills(makeFilters(defaultFilters)));
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

//handleRsltComplete
const showRsltComplete = useBoolean();
const [selectedRsltComplete, setSelectedRsltComplete] = useState(null);
const handleRsltComplete = (param) => {
  setSelectedRsltComplete(param);
  showRsltComplete.onTrue();
};

const handleRsltCompleteClose = () => {
  showRsltComplete.onFalse();
  setSelectedRsltComplete(null);
};

// const [selectedRsltItem, setSelectedRsltItem] = useState(null);
// const [removeRsltVisible, setRemoveRsltVisible] = useState(false);
// const [itemRsltType, setItemRsltType] = useState(null);

// const handleRsltRemove = useCallback((row) => {
//   setRemoveRsltVisible(true);
//   setSelectedRsltItem(row);
// }, []);


// const onRemoveRsltCancle = () => {
//   setRemoveRsltVisible(false);
//   setSelectedRsltItem(null);
// };
// const onRemoveRsltConfirm = () => {
//   //매출 취소
//   onRemove();
//   setRemoveRsltVisible(false);
//   setSelectedRsltItem(null);
// };



const handleRsltCompleteSave = async () => {
  try {
    const editData = {
      invUuid: selectedRsltComplete.invUuid,
      rsltCompYn: 'Y',
    };
    const response = await taxApi.editTaxbill(editData);
    if (response.data.info) {
      handleRsltCompleteClose();
      enqueueSnackbar('실적확정 완료 되었습니다', {
        variant: 'success',
      });
      dispatch(listTaxbills(makeFilters(defaultFilters)));
    }
  } catch (err) {
    let message =
      typeof err.response !== 'undefined'
        ? err.response.data.message
        : err.message;
    enqueueSnackbar(message, {
      variant: 'error',
    });
  }
};


const [selectedRsltCancle, setSelectedRsltCancle] = useState(null);
  const showRsltCancle = useBoolean();
  const handleRsltCancle = useCallback((row) => {
    showRsltCancle.onTrue();
    setSelectedRsltCancle(row);
  }, []);

  const onCancleRsltCancle = () => {
    showRsltCancle.onFalse();
    setSelectedRsltCancle(null);
  };
  const onCancleRsltConfirm = () => {
    //발급 취소
    onRsltCancle();
    showCancle.onFalse();
    setSelectedRsltCancle(null);
  };

  
  const onRsltCancle = async () => {
    try {
      const editData = {
        invUuid: selectedRsltCancle.invUuid,
        rsltCompYn: 'N',
      };
      const response = await taxApi.editTaxbill(editData);
      if (response.data.info) {
        enqueueSnackbar('발급 취소 되었습니다', {
          variant: 'success',
        });
        dispatch(listTaxbills(makeFilters(defaultFilters)));
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

 

//발급 
  const showComplete = useBoolean();
  const [selectedComplete, setSelectedComplete] = useState(null);
  const handleComplete = (param) => {
    setSelectedComplete(param);
    showComplete.onTrue();
  };

  const handleCompleteClose = () => {
    showComplete.onFalse();
    setSelectedComplete(null);
  };

  const handleCompleteSave = async () => {
    try {
      const editData = {
        invUuid: selectedComplete.invUuid,
        reqCompYn: 'Y',
      };
      const response = await taxApi.editTaxbill(editData);
      if (response.data.info) {
        handleCompleteClose();
        enqueueSnackbar('발급확인 되었습니다', {
          variant: 'success',
        });
        dispatch(listTaxbills(makeFilters(defaultFilters)));
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    taxApi.getIssueFlowConfig()
      .then((res) => setApprovalEnabled(Boolean(res?.data?.approvalEnabled)))
      // 설정을 못 읽으면 결재 미사용으로 본다. 담당자가 직접 처리하는 쪽이 덜 막힌다.
      .catch(() => setApprovalEnabled(false));
  }, []);

  /**
   * 발행 요청.
   * 예전에는 [발행] 한 번에 발행완료까지 갔다. 요청 단계가 통째로 건너뛰어졌다.
   */
  const handleIssueRequest = async (row) => {
    try {
      await taxApi.requestIssue(row.invUuid);
      enqueueSnackbar('발행 요청했습니다.', { variant: 'success' });
      dispatch(listTaxbills(makeFilters(defaultFilters)));
    } catch (err) {
      const message = err?.response?.data?.message || err?.message;
      enqueueSnackbar(message || '발행 요청에 실패했습니다.', { variant: 'error' });
    }
  };

  /** 발행 완료. 결재를 쓰는 설정이면 서버가 막는다. */
  const handleIssueComplete = async (row) => {
    try {
      await taxApi.completeIssue(row.invUuid);
      enqueueSnackbar('발행 완료 처리했습니다.', { variant: 'success' });
      dispatch(listTaxbills(makeFilters(defaultFilters)));
    } catch (err) {
      const message = err?.response?.data?.message || err?.message;
      enqueueSnackbar(message || '발행 완료 처리에 실패했습니다.', { variant: 'error' });
    }
  };

  const columns = useMemo(
    () => [
      { field: 'id', hide: true, sortable: false, editable: false },
      { field: 'invUuid', hide: true, sortable: false, editable: false }, //세금계산서ID
      { field: 'roUuid', hide: true, sortable: false, editable: false }, //수주ID
      { field: 'billUuid', hide: true, sortable: false, editable: false }, //거래ID
      { field: 'prjUuid', hide: true, sortable: false, editable: false }, //프로젝트ID
      {
        field: 'invYn',
        hide: true,
        editable: false,
      },
      {
        field: 'invNo',
        headerName: '계산서번호',
        hide: true,
        editable: false,
      },
      {
        field: 'invTyp',
        headerName: '유형',
        hide: true,
        editable: false,
      },

      {
        field: 'reqPlanDt',
        headerName: '발행예정일',
        description: '',
        align: 'center',
        headerAlign: 'center',
        type: 'date',
        flex: 0.8,
        minWidth: 150,
        valueGetter: (params) => {
          // 문자열 형식의 날짜를 Date 객체로 변환
          return params ? new Date(params) : null;
        },
        renderCell: (params) => {
          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle2">{params.row.reqPlanDt}</Typography>
              <IconButton
                aria-label="edit"
                color="primary"
                size="small"
                onClick={() => handleEdit(params.row, 'reqPlan')}
              >
                <EditIcon />
              </IconButton>
            </Stack>
          );
        },
      },
      {
        field: 'reqCompDt',
        headerName: '발행완료일',
        description: '',
        align: 'center',
        headerAlign: 'center',
        type: 'string',
        flex: 0.8,
        minWidth: 150,
        valueGetter: (params) => {
          // 문자열 형식의 날짜를 Date 객체로 변환
          return params ? new Date(params) : null;
        },
        renderCell: (params) => {
          
          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle2">{params.row.reqCompDt}</Typography>
              <IconButton
                aria-label="edit"
                color="primary"
                size="small"
                onClick={() => handleEdit(params.row, 'reqComp')}
              >
                <EditIcon />
              </IconButton>
            </Stack>
          );
        },
      },
      {
        field: 'action',
        headerName: 'action',
        minWidth: 100,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const row = params.row;
          let renderAction;
          // 발행은 요청 → 완료 2단계다. 예전에는 버튼 하나로 완료까지 갔다.
          if (row.reqCompYn === 'Y') {
            if (row.procCompYn === 'Y') {
              renderAction = (
                <Typography
                  variant="subtitle2"
                  color="success.main"
                  sx={{ mx: 1 }}
                >
                  수금완료
                </Typography>
              );
            } else if (row.procCompYn === 'N') {
              renderAction = (
                <Button
                  size="small"
                  variant="contained"
                  color="inherit"
                  onClick={() => handleCancle(row)}
                >
                  발행취소
                </Button>
              );
            }
          } else if (row.reqPlanYn !== 'Y') {
            renderAction = (
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={() => handleIssueRequest(row)}
              >
                발행요청
              </Button>
            );
          } else if (approvalEnabled) {
            // 결재를 쓰는 설정이면 승인으로만 완료된다. 여기서 임의로 완료시키면 절차가 무너진다.
            renderAction = (
              <Typography variant="subtitle2" color="warning.main" sx={{ mx: 1 }}>
                결재 진행중
              </Typography>
            );
          } else {
            renderAction = (
              <Button
                size="small"
                variant="contained"
                color="success"
                onClick={() => handleIssueComplete(row)}
              >
                발행완료
              </Button>
            );
          }

          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              {renderAction}
              {/* 대금이 나눠 들어오므로, 발행된 건은 언제든 수금 내역을 보고 넣을 수 있어야 한다.
                  수금완료 건도 내역 확인이 필요해 함께 노출한다. */}
              {row.reqCompYn === 'Y' && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setCollectTarget({ invUuid: row.invUuid, invTyp: row.invTyp })}
                >
                  수금
                </Button>
              )}
              {/* <Button
                size="small"
                variant="contained"
                color="error"
                onClick={() => handleRemove(params.row)}
              >
                취소
              </Button> */}
            </Stack>
          );
        },
      },
      {
        field: 'rsltCompDt',
        headerName: '실적반영일',
        description: '',
        align: 'center',
        headerAlign: 'center',
        type: 'date',
        flex: 0.8,
        minWidth: 150,
        valueGetter: (params) => {
          // 문자열 형식의 날짜를 Date 객체로 변환
          return params ? new Date(params) : null;
        },
        renderCell: (params) => {
          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle2">{params.row.rsltCompDt}</Typography>
              <IconButton
                aria-label="edit"
                color="primary"
                size="small"
                onClick={() => handleEdit(params.row, 'rsltComp')}
              >
                <EditIcon />
              </IconButton>
            </Stack>
          );
        },
      },
      {
        field: 'action2',
        headerName: 'Action 2',
        minWidth: 100,
        flex: 1,
        editable: false,
        align: 'center',
        headerAlign: 'center',

        renderCell: (params) => {
          const { reqCompYn, rsltCompYn } = params.row;
      
          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              {reqCompYn === 'Y' && rsltCompYn === 'N' && (
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  onClick={() => handleRsltComplete(params.row)}
                >
                  실적확정
                </Button>
              )}
              {reqCompYn === 'Y' && rsltCompYn === 'Y' && (
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  onClick={() => handleRsltCancle(params.row)}
                >
                  확정취소
                </Button>
              )}
            </Stack>
          );
        },
      },
      {
        field: 'prjNo',
        headerName: '프로젝트번호',
        minWidth: 150,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'center',
      },
      {
        field: 'partnerNm',
        headerName: '고객명',
        minWidth: 150,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'center',
      },

      {
        field: 'cstNm',
        headerName: '사업명',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'center',
      },
      {
        field: 'invTxt',
        headerName: '발급내용',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'invAmt',
        headerName: '계산서 금액',
        minWidth: 120,
        flex: 1,
        type: 'number',
      },
      {
        field: 'invVat',
        headerName: '부가세 금액',
        minWidth: 120,
        flex: 1,
        type: 'number',
      },
      {
        field: 'invTot',
        headerName: '합계 금액',
        minWidth: 150,
        flex: 1,
        type: 'number',
      },

      {
        field: 'procCompDt',
        headerName: '지급(수금)완료일자',
        description: '',
        align: 'center',
        headerAlign: 'center',
        flex: 0.8,
        minWidth: 180,
      },
      {
        field: 'procCompYn',
        headerName: '지급(수금) 완료 여부',
        description: '',
        align: 'center',
        headerAlign: 'center',
        flex: 0.8,
        minWidth: 120,
      },

      {
        field: 'reqPlanUsrNm',
        headerName: '발행요청자',
        description: '발행요청자',
        type: 'string',
        flex: 0.8,
        minWidth: 120,
      },

      {
        field: 'salesUsrNm',
        headerName: '영업사원',
        description: '영업사원',
        type: 'string',
        flex: 0.8,
        minWidth: 120,
      },
    ],
    [approvalEnabled],
  );

  //검색 관련 사항
  const initialValues = {
    searchKeyword: defaultFilters.searchKeyword,
    searchWords: defaultFilters.searchWords,
    searchStrtDt: defaultFilters.searchStrtDt,
    searchEndDt: defaultFilters.searchEndDt,
  };

  const validationSchema = yup.object({
    searchKeyword: yup.string('').nullable(),
    searchWords: yup.string('').trim().nullable(),
    searchStrtDt: yup.date().nullable().default(undefined),
    searchEndDt: yup
      .date()
      .min(yup.ref('searchStrtDt'), '검색 종료일을 확인하세요')
      .when('searchStrtDt', (searchStrtDt, schema) => {
        if (searchStrtDt) return schema.required('검색 종료일을 입력하세요');
      })
      .nullable()
      .default(undefined),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);
      const searchData = {
        searchOsTran: defaultFilters.searchOsTran,
        searchInvTyp: defaultFilters.searchInvTyp,
        searchTyp: defaultFilters.searchTyp,
        reqCompYn: defaultFilters.searchYn,
        searchKeyword: values.searchKeyword,
        searchWords: values.searchWords,
        searchStrtDt: values.searchStrtDt,
        searchEndDt: values.searchEndDt,
      };
      dispatch(setTaxbillsFilters(searchData));
      setTimeout(() => {
        setSubmitting(false);
      }, 1000);
    },
  });

  return (
    <>
      <BreadcrumbsContainer
        title="계산서 채권채무/세금계산서 관리/세금계산서 리스트"
        icon="pay"
      />
      <TaxbillListBox formik={formik} />
      <TaxbillStatusList filters={defaultFilters} onFilters={handleFilters} />
      <TaxbillTotalSummary totalPrice={totalPrice} />
      <TaxbillList
        columns={columns}
        rows={tableData}
        error={error}
        loading={loading}
        columnVisibilityModel={{
          id: false,
          invNo: false,
          invUuid: false,
          roUuid: false,
          billUuid: false,
          prjUuid: false,
          invYn: false,
          invTyp: false,
        }}

      />
      <AskDialog
        visible={removeVisible}
        title="매출 취소"
        description={`해당 매출 "${selectedItem?.invTxt}" 을 취소하시겠습니까?`}
        onConfirm={onRemoveConfirm}
        onCancle={onRemoveCancle}
      />
      <AskDialog
        visible={showCancle.value}
        title="발급 취소"
        description={`해당 매출 "${selectedCancle?.invTxt}" 을 발급 취소하시겠습니까?`}
        onConfirm={onCancleConfirm}
        onCancle={onCancleCancle}
      />
      <AskDialog
        visible={showComplete.value}
        title="발급 완료"
        description={`해당 매출 "${selectedComplete?.invTxt}" 을 발급 완료하시겠습니까?`}
        onConfirm={handleCompleteSave}
        onCancle={handleCompleteClose}
      />
      <AskDialog
        visible={showRsltComplete.value}
        title="실적 확정 완료"
        description={`해당 매출 "${selectedRsltComplete?.invTxt}" 을 실적 확정하시겠습니까?`}
        onConfirm={handleRsltCompleteSave}
        onCancle={handleRsltCompleteClose}
      />
      <AskDialog
        visible={showRsltCancle.value}
        title="실적 확정 취소"
        description={`해당 매출 "${selectedRsltComplete?.invTxt}" 을 실적 확정을 취소하시겠습니까?`}
        onConfirm={onCancleRsltConfirm}
        onCancle={onCancleRsltCancle}
      />
      {showEdit && (
        <TaxbillEditModal
          currentData={selectedEdit}
          open={showEdit.value}
          handleClose={handleEditClose}
          onSave={(changeDate, type) => handleEditSave(changeDate, type)}
          editType={itemType} 
        />
      )}
      <BillCollectDialog
        open={Boolean(collectTarget)}
        invUuid={collectTarget?.invUuid}
        invTyp={collectTarget?.invTyp}
        onClose={() => setCollectTarget(null)}
        // 수금 상태가 바뀌면 목록의 수금완료 표시도 같이 맞춰야 한다.
        onSaved={() => dispatch(listTaxbills(makeFilters(defaultFilters)))}
      />
    </>
  );
};

export default TaxbillListContainer;
